import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useHomeContext } from "./HomeContext";
import { Product } from "./types";
import config from "./config";
import { parseColorName, extractColorsFromText } from "./utils/colorUtils";

type Feature = {
  Feature_Name: string;
  Feature_Value: string;
};

const ProductDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products } = useHomeContext();
  const product = products.find(p => p.id === Number(id));

  const [apiProduct, setApiProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (id) {
      fetch(`${config.apiBaseUrl}/api/productDB/${id}`)
        .then(res => res.json())
        .then(data => setApiProduct(data))
        .catch(() => setApiProduct(null))
        .finally(() => setLoading(false));
    }
  }, [id]);

  const displayProduct = apiProduct || product;

  // Parse featuresJson if available
  let features: Feature[] = [];
  if (displayProduct?.featuresJson) {
    try {
      features = JSON.parse(displayProduct.featuresJson);
    } catch {
      features = [];
    }
  }

  // Fetch related products by name
  useEffect(() => {
    if (displayProduct?.name) {
      setLoadingRelated(true);
      const params = new URLSearchParams({ name: displayProduct.name });
      fetch(`${config.apiBaseUrl}/api/productDB/filter?${params.toString()}`)
        .then(res => res.json())
        .then(data => setRelatedProducts(data))
        .catch(() => setRelatedProducts([]))
        .finally(() => setLoadingRelated(false));
    }
  }, [displayProduct?.name]);

  return (
    <div style={{ 
      display: "flex", 
      flexDirection: isMobile ? 'column' : 'row',
      flexWrap: "wrap",
      gap: isMobile ? 16 : 32, 
      maxWidth: 1200, 
      margin: "0 auto", 
      padding: isMobile ? 16 : 24
    }}>
      <div style={{ 
        flex: isMobile ? '1' : "2 1 600px", 
        minWidth: 0,
        textAlign: 'left' 
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginBottom: '30px',
            padding: window.innerWidth < 480 ? '8px 16px' : '12px 24px',
            backgroundColor: '#2c5aa0',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#1e3d72';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#2c5aa0';
          }}
        >
          ← Back to List
        </button>
        {loading ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '60vh',
            fontSize: '1.2rem',
            color: '#666',
            padding: '40px 20px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px'
            }}>
              {/* Loading Spinner */}
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #2c5aa0',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              
              {/* Loading Text */}
              <div style={{
                fontSize: '1.1rem',
                fontWeight: '500'
              }}>
                Loading Product Details...
              </div>
              
              {/* Animated Dots */}
              <div style={{
                fontSize: '1.5rem',
                letterSpacing: '2px',
                animation: 'dots 1.5s infinite'
              }}>
                • • •
              </div>
            </div>
            
            {/* CSS Animation Styles */}
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              
              @keyframes dots {
                0%, 20% { opacity: 0; }
                50% { opacity: 1; }
                100% { opacity: 0; }
              }
            `}</style>
          </div>
        ) : displayProduct ? (
          <><h2 style={{ fontSize: isMobile ? '1.5rem' : '2rem' }}><strong>{displayProduct.name}</strong></h2><br></br>
            <img 
              src={displayProduct.imageUrl} 
              alt={displayProduct.name} 
              style={{ width: '100%', maxWidth: isMobile ? '100%' : 500, maxHeight: isMobile ? 300 : 400, objectFit: 'cover', borderRadius: 12, marginBottom: 24, display: 'block' }} 
            />
            <div style={{ textAlign: 'left' }}>
              <br></br>
              {/* <h2 style={{ fontSize: '2rem' }}><strong>{displayProduct.name}</strong></h2> */}
              <div><strong>Brand:</strong> {displayProduct.brand}</div>
              <div><strong>Model:</strong> {displayProduct.model}</div>
              <br></br>
              <div style={{ fontWeight: 'bold', fontSize: 18, margin: '8px 0' }}>
                {typeof displayProduct.originalPrice === 'number' && typeof displayProduct.salePrice === 'number' ? (
                  <span>
                    <span style={{ textDecoration: displayProduct.originalPrice > displayProduct.salePrice ? 'line-through' : 'none', color: displayProduct.originalPrice > displayProduct.salePrice ? '#888' : undefined }}>
                      <span style={{ fontWeight: 400, marginRight: 4 }}>Original:</span>${displayProduct.originalPrice}
                    </span>
                    <span style={{ color: displayProduct.originalPrice > displayProduct.salePrice ? '#2563eb' : undefined, fontWeight: displayProduct.originalPrice > displayProduct.salePrice ? 600 : undefined, marginLeft: 12 }}>
                      <span style={{ fontWeight: 400, marginRight: 4 }}>Sale:</span>${displayProduct.salePrice}
                    </span>
                    {displayProduct.originalPrice > displayProduct.salePrice && (
                      <span style={{ color: 'green', fontSize: '15px', marginLeft: 8 }}>
                        Save ${(displayProduct.originalPrice - displayProduct.salePrice).toFixed(2)}
                        ({Math.round(((displayProduct.originalPrice - displayProduct.salePrice) / displayProduct.originalPrice) * 100)}% off)
                      </span>
                    )}
                  </span>
                ) : (
                  <>${displayProduct.price}</>
                )}
              </div>
              {displayProduct.gender && (
                <div style={{ fontSize: 15, marginBottom: 4 }}>
                  <b>Gender:</b> {displayProduct.gender}
                </div>
              )}
              {(displayProduct.size || displayProduct.width) && (
                <div style={{ fontSize: 15, marginBottom: 4 }}>
                  {displayProduct.size && <div><b>Size:</b> {displayProduct.size}</div>}
                  {displayProduct.width && <div><b>Width:</b> {displayProduct.width}</div>}
                </div>
              )}
              {displayProduct.retailer && (
                <div style={{ fontSize: 15, marginBottom: 4 }}>
                  <b>Retailer:</b> {displayProduct.retailer}
                  {displayProduct.retailerSku && <> | <b>SKU:</b> {displayProduct.retailerSku}</>}
                </div>
              )}
              <br></br>
              <div style={{ margin: '16px 0' }}>
                <a
                  href={displayProduct.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#2563eb', 
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '16px'
                  }}
                >
                  View Product
                </a>
              </div>
              <div>
                <strong>Description:</strong>
                <div dangerouslySetInnerHTML={{ __html: displayProduct.description.replace(/Available Colors:[^<]*(<[^>]*>)*[^<]*/gi, '') }} />

                {/* Color Swatches */}
                {displayProduct.description && extractColorsFromText(displayProduct.description).length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{ marginBottom: '12px', fontWeight: '600', fontSize: '14px' }}>Available Colors:</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                      {extractColorsFromText(displayProduct.description).map((color: string, idx: number) => {
                        const hexColor = parseColorName(color);
                        return (
                          <div
                            key={idx}
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              backgroundColor: hexColor,
                              border: '2px solid #e0e0e0',
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            title={color}
                            onMouseEnter={(e) => {
                              const element = e.currentTarget as HTMLDivElement;
                              element.style.transform = 'scale(1.15)';
                              element.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                              const element = e.currentTarget as HTMLDivElement;
                              element.style.transform = 'scale(1)';
                              element.style.boxShadow = 'none';
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Additional Images */}
              {displayProduct.imageUrls && (
                <div style={{ marginTop: 24 }}>
                  <h3>Additional Images</h3>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(150px, 1fr))' : 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: isMobile ? 12 : 16,
                    marginTop: 16
                  }}>
                    {displayProduct.imageUrls.split(',').map((imageUrl, index) => (
                      <img
                        key={index}
                        src={imageUrl.trim()}
                        alt={`${displayProduct.name} - Image ${index + 1}`}
                        style={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: '1px solid #e5e7eb'
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            
            </div>
            {/* Features Table */}
            {features.length > 0 && (
              <div style={{ marginTop: 32, overflowX: isMobile ? 'auto' : 'visible' }}>
                <h3>Features</h3>
                <table style={{ width: "100%", minWidth: isMobile ? 400 : 'auto', borderCollapse: "collapse", background: "#fff" }}>
                  <thead>
                    <tr style={{ background: "#f3f4f6" }}>
                      <th style={{ padding: isMobile ? 6 : 8, border: "1px solid #ddd", fontSize: isMobile ? 13 : 14 }}>Feature</th>
                      <th style={{ padding: isMobile ? 6 : 8, border: "1px solid #ddd", fontSize: isMobile ? 13 : 14 }}>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((f, i) => (
                      <tr key={i}>
                        <td style={{ padding: isMobile ? 6 : 8, border: "1px solid #ddd", fontWeight: 600, fontSize: isMobile ? 12 : 14 }}>{f.Feature_Name}</td>
                        <td style={{ padding: isMobile ? 6 : 8, border: "1px solid #ddd", fontSize: isMobile ? 12 : 14 }}>{f.Feature_Value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <div>Product not found.</div>
        )}
      </div>
      <div style={{
        flex: isMobile ? '1' : "1 1 300px",
        minWidth: isMobile ? 'auto' : 300,
        background: "#f9fafb",
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        padding: isMobile ? 16 : 24,
        minHeight: isMobile ? 'auto' : 300
      }}>
        <h3 style={{ textAlign: 'left', margin: '0 0 16px 0', fontSize: isMobile ? '1.2rem' : '1.5rem' }}>Related Products</h3>
        {loadingRelated ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            fontSize: '1rem',
            color: '#666',
            padding: '20px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '15px'
            }}>
              {/* Loading Spinner */}
              <div style={{
                width: '30px',
                height: '30px',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #2c5aa0',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              
              {/* Loading Text */}
              <div style={{
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                Loading related products...
              </div>
            </div>
          </div>
        ) : relatedProducts.length === 0 ? (
          <div>No related products found.</div>
        ) : (
          <ul style={{ listStyle: "none", padding: 0 }}>
            {relatedProducts.map((prod) => (
              <li key={prod.id} style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                marginBottom: 20,
                background: "#fff",
                borderRadius: 6,
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                padding: 12
              }}>
                <img src={prod.imageUrl} alt={prod.name} style={{ width: 60, maxHeight: 60, objectFit: "cover", borderRadius: 4, alignSelf: 'flex-start', marginTop: 4 }} />
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ fontWeight: 600 }}>{prod.name}</div>
                  <div style={{ fontSize: 14, color: "#555" }}>{prod.brand}</div>
                  <div style={{ fontSize: 14, color: "#555" }}>{prod.retailer}</div>
                  <div style={{ fontSize: 14, color: "#555" }}>${prod.salePrice}</div>
                  <Link to={`/product/${prod.id}`} className="text-blue-600 hover:underline" style={{ fontSize: 14, marginTop: 4, display: 'block' }}>
                    View Details
                  </Link>
                </div>
                {prod.retailerLink && (
                  <a
                    href={prod.retailerLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: "#2563eb",
                      color: "#fff",
                      borderRadius: 4,
                      padding: "6px 16px",
                      textDecoration: "none",
                      fontWeight: 600
                    }}
                  >
                    View
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ProductDetails;